import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PeopleIcon from "@mui/icons-material/People";
import GridViewIcon from "@mui/icons-material/GridView";
import { KaerIcon } from "@/icons/kaerIcon";
import type { MRT_Row } from "material-react-table";
import type { RowActionsSlot } from "@/components/tables/rowActions/rowActionsSlot";

type AppointmentRequestRow = MRT_Row<Record<string, any>>;

export interface AppointmentRequestRowActionHandlers {
  t: (key: string) => string;
  openDraft: (row: AppointmentRequestRow) => void;
  reinviteSubjects: (row: AppointmentRequestRow) => void;
  showInvitedPersons: (row: AppointmentRequestRow) => void;
  showExcludedPersons: (row: AppointmentRequestRow) => void;
  editTimeslots: (appointmentRequestId: string) => void;
}

const excludedPersonsIcon = (
  <KaerIcon
    icon="groupOff"
    height="24"
    width="24"
    fill="#6c6c6c"
    stroke="#6c6c6c"
    strokeWidth="0"
  />
);

export const appointmentRequestRowActions =
  (handlers: AppointmentRequestRowActionHandlers): RowActionsSlot =>
  (row, { standardActions }) => {
    const { t } = handlers;
    const { status, day_set: daySet, id } = row.original;
    const isDraft = status === "draft";
    const isDraftWithoutDays = isDraft && daySet?.length === 0;
    const actions = [...standardActions];

    if (isDraft || status === "open") {
      actions.push({
        key: "statusChange",
        icon: isDraft ? <CheckIcon /> : <GroupAddIcon />,
        label: isDraftWithoutDays
          ? t("appointmentRequest_day_required")
          : isDraft
          ? t("status_change")
          : t("reinvite_subjects"),
        onClick: () =>
          isDraft ? handlers.openDraft(row) : handlers.reinviteSubjects(row),
        disabled: isDraftWithoutDays,
        wrapButtonInSpan: true,
        testId: "appointment-request-status-change-action", // used by cypress/e2e/01_createNewAppointmentRequest.cy.ts
      });
    }

    if (status === "open") {
      actions.push(
        {
          key: "showInvitedPersons",
          icon: <PeopleIcon />,
          label: t("show_invited"),
          onClick: () => handlers.showInvitedPersons(row),
        },
        {
          key: "showExcludedPersons",
          icon: excludedPersonsIcon,
          label: t("show_excluded"),
          onClick: () => handlers.showExcludedPersons(row),
        },
        {
          key: "editTimeslots",
          icon: <GridViewIcon />,
          label: t("edit_available_timeslots"),
          onClick: () => handlers.editTimeslots(id),
        }
      );
    }

    return actions;
  };
